/**
 * tooltip — a description attached to any element, promoted into the
 * browser's TOP LAYER (`popover="hint"` + `showPopover()`) so it paints
 * above anything already promoted into the top layer at the moment it
 * shows — including another schmancy overlay's own top-layer surface it
 * sits directly on. Top-layer ordering is PROMOTION order (an ordered set,
 * appended on show), a stacking property the top layer simply has none of,
 * so this file sets none either — there is none to set, and none to lose.
 * That guarantee only runs the one direction: something promoted AFTER the
 * tooltip is already showing would paint over it, so the tooltip dismisses
 * itself the instant any other element enters the top layer, or its own
 * trigger is pressed — it never lingers behind a dialog, drawer, or menu
 * opened while already shown. The press half of that arms EARLIER than
 * the other two — from `attach()`, over the show's own PENDING window,
 * not only while it is up. `timer(0)` is still a macrotask, so even at
 * the default `delay` of 0 every show has a turn in which it is
 * scheduled and `reveal()` has not run, and a press landing in that turn
 * is seen by nothing if the guard exists only once the tooltip is up: the
 * tooltip reveals ON TOP of whatever that press just opened, and the
 * guards it registers at that moment watch only for the NEXT top-layer
 * entry, which never comes. The other two deliberately stay shown-only,
 * because their reason is promotion ORDER and a tooltip that has not been
 * promoted yet already wins it: something entering the top layer while a
 * show is merely pending is promoted BEFORE the tooltip, so the tooltip
 * paints above it — the guarantee this paragraph opens with, not a
 * violation of it. Two composed signals, not one: a capture-phase
 * `toggle` on `document` catches a popover promoted into the SAME tree this
 * listener sits in (light DOM, or the trigger's own shadow root), but
 * `ToggleEvent.composed` is `false` — measured, in both engines, to never
 * reach a listener outside the shadow root the popover itself lives in — and
 * every real schmancy overlay's surface lives inside ITS OWN component's
 * shadow root, so `toggle` alone never sees one. `focusin` on `document`
 * covers that gap: `focus`/`focusin` ARE composed, and every overlay tier
 * moves focus into its own surface on open (`overlay.component.ts`'s
 * `wireFocusTrap`), so a `focusin` whose real origin — `composedPath()[0]`,
 * which (unlike the retargeted `event.target`) survives shadow boundaries —
 * lands inside a `:popover-open` ancestor is the second signal.
 *
 * Positioned with CSS Anchor Positioning (`anchor-name` / `position-anchor`
 * / `position-area` / `position-try-fallbacks`) wherever the engine supports
 * it — zero JS running per frame, and the position resolves in the SAME
 * layout pass that removes the popover's `display:none`, so there is no
 * frame at a wrong position to guard against. `CSS.supports('anchor-name:
 * --x')`, read via schmancy's shared `CAPS`, gates a `getBoundingClientRect`
 * fallback, refreshed on capture-phase scroll/resize while shown — the one
 * path where a wrong-position frame is structurally possible.
 *
 * `anchor-name` is a comma-separated `<dashed-ident>#` list, not a single
 * value — the SAME CSS property another writer on the shared trigger (a
 * `<schmancy-menu>` anchoring itself to the same button, say) also sets.
 * This file never overwrites it: every reveal APPENDS this instance's own
 * `--tt-<uid>` token if it's missing (self-healing a token another writer's
 * save-and-restore may have dropped while the tooltip stayed open), and
 * teardown removes exactly that token, leaving any other writer's tokens on
 * the property untouched — the same append/remove-exact-token discipline
 * this file already applies to `aria-describedby`.
 *
 * The floating element lives in the SAME root the trigger resolves
 * `anchor-name` in — the trigger's own `ShadowRoot` when it has one, else
 * `document.body` — appended at the end of that root, outside Lit's part
 * markers so a re-render never touches it. `anchor-name` is tree-scoped: it
 * is looked up in the reference's own root and walks up through shadow
 * hosts, but never descends INTO a shadow tree, so a tooltip built in
 * `document.body` for a shadow-DOM trigger would never resolve its anchor.
 *
 * Show and hide both run a WAAPI entrance/exit built from the library's own
 * Blackbird system (`packages/utils/src/animation.ts`) — `SPRING_SNAPPY`
 * (300ms) in, that same preset at `DURATION_EXIT` (150ms) out — scale +
 * opacity only, never a directional translate: `position-try-fallbacks` can
 * flip which side the tooltip renders on with zero JS and nothing in CSS
 * tells the animation which side won, so a fixed `translateY` would animate
 * the wrong way after a flip. `showPopover()` and the entrance's
 * `element.animate()` call happen on the SAME frame — no staged delay
 * before the tooltip becomes visible; `delay` (default 0) still only
 * staggers the SHOW of a hover-driven reveal, `:focus-visible` keyboard
 * focus shows on the next tick as before. Hide is WAAPI-driven too, with
 * `hidePopover()` called on the exit animation's own `finished` — never a
 * CSS `transition-behavior: allow-discrete` + `overlay` transition, since
 * `overlay` is unsupported in both Firefox and Safari (Chromium-only) and a
 * CSS exit would drop out of the top layer on the same frame there,
 * meaning the exit would never be seen outside Chromium. A re-show mid-exit
 * cancels the pending exit AND its queued `hidePopover()` in one move —
 * `Animation.cancel()` rejects `finished`, so the queued close never fires.
 * Teardown (`detach()`/`disconnected()`) cancels whatever is in flight and
 * hides synchronously rather than animating out an unmounting host.
 *
 * A canceled WAAPI animation snaps its target to the REPLACEMENT
 * animation's own first keyframe on the very next frame — `fill:'forwards'`
 * only pins the finished value, gone the instant `.cancel()` runs — so a
 * restart built from the same fixed 0/0.9 or 1/1 endpoints as a fresh call
 * is wrong twice over: `reveal()` on an already-visible tooltip (its
 * `reason` merely changing, e.g. pointer → keyboard focus landing on the
 * same trigger the mouse is still resting on) is not a visual event and
 * must not re-run the entrance at all; `hide()` racing a mid-flight
 * entrance must continue the exit from wherever that entrance actually
 * was, not from a hardcoded settled value. `reveal()` skips the entrance
 * outright on anything but a genuine hidden→shown transition; both
 * directions read the box's own live opacity/scale (`readLiveScaleState`)
 * as the replacement's `from` whenever an animation is actually in flight
 * to replace.
 *
 * Hoverable (WCAG 2.2 SC 1.4.13) with no timer: the outer, popover-bearing
 * box carries `border: 8px solid transparent` and paints nothing of its
 * own — the visible pill is a nested, unbordered child, inset from that
 * outer box's own hit rect by exactly the border's width — so the pill
 * LOOKS like it floats 8px off the trigger while the outer box's actual hit
 * rect touches it — the pointer never crosses dead space moving from one to
 * the other, so there is no "did it leave both" state to debounce with a
 * grace timer. It hides the instant neither
 * the trigger nor the tooltip is hovered or focused; Escape dismisses via
 * its own listener regardless of engine (`hint`'s native light-dismiss
 * covers this only where the value is understood — WebKit's invalid-value
 * default falls back to `manual`, which implements neither auto-dismiss).
 * That same hoverable box is why `hide()` sets the tooltip's own
 * `pointer-events: none` for the exit's duration (`reveal()` restores it):
 * the box itself is shrinking/growing under the exit/entrance scale
 * transform, and left hit-testable, a real cursor resting anywhere near
 * that moving edge would fire a genuine `pointerenter`/`pointerleave` from
 * the geometry change alone — read by the SAME hoverable listener above as
 * a fresh hover, resurrecting the very tooltip the exit is closing.
 * Measured: without this, a dismissed tooltip can oscillate hide↔reveal
 * indefinitely under a stationary cursor sitting in that boundary band.
 *
 * The pill itself is a real `<schmancy-surface type="containerHighest"
 * opacity="solid" elevated>` wrapping a `<schmancy-typography type="body"
 * token="md">`, never hand-rolled `Object.assign(el.style, …)` paint. MD3
 * calls the tooltip token "inverse surface", but "inverse" means the
 * opposite of whichever theme is ACTIVE by definition — a literal inverse
 * surface on a dark-themed page is necessarily a white pill with black
 * text, reading as foreign chrome bolted onto a dark app rather than part
 * of it. `containerHighest` is a tonal surface that follows the active
 * theme instead, exactly like every other schmancy surface. `opacity="solid"`
 * (94% fill) keeps the pill legible over whatever's behind it — a
 * momentary hint label can't afford to gamble legibility on the backdrop
 * the way a `translucent` glass card can. `elevated` turns on the same
 * backdrop-blur every other floating schmancy surface carries, for the
 * literal case its own docblock names — "the one surface that's actually
 * floating" — over a small painted area, cheaply. No box-shadow: MD3's
 * plain tooltip carries no elevation shadow at all, so `elevated`'s blur is
 * the only depth cue, and there is nothing left to reconstruct by hand.
 * Corner radius, padding, and max-width stay inline styles on the pill —
 * `<schmancy-surface>`'s own docblock says sizing is the consumer's
 * responsibility, not something the primitive owns.
 *
 * No arrow. `position-try-fallbacks` can flip which side the tooltip
 * renders on with zero JS, but nothing short of JS can move a decorative
 * arrow child to track that flip, and an arrow that stops pointing at its
 * trigger after a flip is worse than no arrow at all. `showArrow` stays in
 * `TooltipOptions` for call-site compatibility; it does nothing.
 *
 * The tooltip answers to the input that can use it:
 *   - mouse/pen hover shows after `delay`; a touch `pointerenter` never
 *     drives this path — that is the emulated-hover bug on touch devices.
 *   - `:focus-visible` keyboard focus shows immediately; a focus that isn't
 *     focus-visible (tap/click focus) never shows.
 *   - touch does nothing at all. iOS never fires `contextmenu`, WebKit
 *     suppresses `pointercancel` once `touch-action: manipulation` is set,
 *     `pointerleave` cannot fire mid-press because implicit pointer capture
 *     holds the target, and a button never takes focus on tap — every piece
 *     of a long-press mechanism is inert or self-defeating on iOS, and MD3's
 *     own long-press pattern is documented Compose-only, Web Unavailable.
 *
 * Open intent is ONE signal, not two independent arms: hover and keyboard
 * focus combine into a single `reason` (`'keyboard' | 'pointer' | null`),
 * and the tooltip hides only when both are false — hover's teardown can
 * never close a tooltip keyboard focus still holds open.
 *
 * It describes, never names: the floating element carries `role="tooltip"`
 * and the host gets `aria-describedby`, appended (never clobbering a
 * pre-existing value) at reveal and removed at hide, so a control still
 * needs its own accessible name — a tooltip is the second sentence, not the
 * first.
 *
 * The floating element is built lazily, on the first reveal, and reused for
 * the rest of the attachment — a page rendering many tooltips pays nothing
 * until one is actually shown.
 *
 * State lives on the directive INSTANCE rather than a WeakMap keyed by the
 * host element. Lit gives one directive instance per part and holds it
 * across re-renders — two tooltips on one element don't collide on their
 * own subscriptions, and don't collide on `anchor-name` either: it's a
 * token list, and each instance owns only its own token.
 *
 * `AsyncDirective`, not `Directive`: only the async base participates in
 * Lit's disconnection protocol, so a plain `Directive` never has its
 * `disconnected` called at all and every unmounted host would strand its
 * floating element on its root along with its subscription. `reconnected`
 * rebuilds, since a part may be reattached without a re-render —
 * `disconnected` nulls the built element so the rebuild starts clean rather
 * than reusing a node that already left the tree.
 *
 * Teardown is `unsubscribe()`, not `takeUntil` — `AsyncDirective` exposes no
 * `disconnecting` subject of its own. The one pipeline's `Subscription` is
 * stored on the instance in `attach()` and unsubscribed in `detach()`.
 */
import { CAPS } from '@schmancy/overlay/overlay.positioning'
// Side-effect only — registers <schmancy-surface> and <schmancy-typography>
// so document.createElement(...) below returns an upgraded custom element
// rather than an inert, unstyled one (see "the pill is a real schmancy
// primitive" paragraph below).
import '@schmancy/primitives/surface'
import '@schmancy/primitives/typography'
import { createAnimation, DURATION_EXIT, SPRING_SNAPPY } from '@schmancy/utils/animation'
import { noChange } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import { type ElementPart, type PartInfo, PartType } from 'lit/directive.js'
import { BehaviorSubject, combineLatest, EMPTY, from, fromEvent, merge, of, Subscription, timer } from 'rxjs'
import {
	catchError,
	distinctUntilChanged,
	filter,
	map,
	startWith,
	switchMap,
	take,
	takeUntil,
	tap,
} from 'rxjs/operators'

export interface TooltipOptions {
	position?: 'top' | 'right' | 'bottom' | 'left'
	delay?: number
	/** @deprecated Accepted for call-site compatibility only; the top-layer
	 *  redesign carries no arrow (see the file header), so this has no effect. */
	showArrow?: boolean
}

type OpenReason = 'keyboard' | 'pointer' | null

/** Visible gap between trigger and tooltip, held open by a transparent
 *  border on the tooltip's own box (see `reveal()`) rather than a margin —
 *  a margin leaves dead space the pointer must jump across to satisfy WCAG
 *  2.2 SC 1.4.13 Hoverable; a border is part of the box's own hit rect, so
 *  the gap is never actually "outside" the tooltip. */
const GAP_PX = 8

/** The pill's own border-radius, applied directly to the painted
 *  `<schmancy-surface>` pill's border-box — the GAP_PX transparent border
 *  lives on the wrapping popover element instead (see `reveal()`), so this
 *  radius needs no inflation math to land on what a reader actually sees,
 *  whatever hairline border the surface's own glass material draws inside
 *  it. The `xs` rung — the ladder's micro corner, shared with kbd/badges/
 *  skeleton text/swatches — reads as a label, not a chip/pill. */
const TOOLTIP_RADIUS = 'var(--schmancy-radius-xs, 0.4375rem)'

class TooltipDirective extends AsyncDirective {
	private element?: HTMLElement

	// The live render arguments. Every handler reads these AT SHOW TIME rather
	// than closing over the `update()` call that wired it up — a host whose
	// label changes between renders (an order's delivery status advancing, say)
	// would otherwise keep showing the text it had when first rendered.
	private text = ''
	private position: NonNullable<TooltipOptions['position']> = 'top'
	private delay = 0

	// Built lazily on the first reveal, reused for the rest of the attachment.
	private tooltipElement: HTMLElement | null = null
	/** The painted pill nested inside `tooltipElement` — a `<schmancy-surface>`
	 *  wrapping a `<schmancy-typography>`; see `reveal()`'s build branch for
	 *  why the visual chrome lives one level deeper than the popover/hit-area
	 *  box it's nested in. */
	private pillElement: HTMLElement | null = null
	private textNode: Text | null = null
	/** Set only when CSS anchor positioning built the tooltip — the CSS
	 *  custom-ident this instance owns on the trigger's `anchor-name`, so
	 *  `detach()` knows whether there is one to clean up. */
	private anchorName: string | null = null
	private describedByAdded = false
	private visible = false

	private tooltipEl$ = new BehaviorSubject<HTMLElement | null>(null)
	private subscription?: Subscription
	/** Only ever populated on the `!CAPS.cssAnchor` fallback path. */
	private fallbackReposition?: Subscription
	private escapeSubscription?: Subscription
	/** Live only while shown — hides the tooltip the instant something else
	 *  enters the top layer, or the trigger itself is pressed. See the file
	 *  header's "That guarantee only runs the one direction" paragraph. */
	private dismissSubscription?: Subscription

	/** Whichever WAAPI animation is currently in flight — the entrance
	 *  `reveal()` starts or the exit `hide()` starts — so either method can
	 *  cancel what the other left running before starting its own. Canceling
	 *  an exit rejects its `finished` promise, which is how a re-show mid-exit
	 *  also cancels its queued `hidePopover()` (see `exitSubscription`). */
	private currentAnimation: Animation | null = null
	/** The exit animation's own `finished` → `hidePopover()` pipeline —
	 *  re-created on every `hide()`, unlike `subscription`'s once-per-attach
	 *  chain, so it gets its own field rather than joining that one. */
	private exitSubscription?: Subscription

	constructor(partInfo: PartInfo) {
		super(partInfo)
		if (partInfo.type !== PartType.ELEMENT) {
			throw new Error('The tooltip directive can only be used on elements')
		}
	}

	render(_text: string, _options: TooltipOptions = {}) {
		return noChange
	}

	override update(part: ElementPart, [text, options = {}]: [string, TooltipOptions?]) {
		this.element = part.element as HTMLElement
		this.text = text
		this.position = options.position ?? 'top'
		this.delay = options.delay ?? 0

		// Already built — refresh only what may be on screen right now; the
		// next reveal reads the new values off `this` by itself.
		if (this.textNode) this.textNode.data = this.text

		if (!this.subscription) this.attach()

		return noChange
	}

	private attach(): void {
		const element = this.element
		if (!element || this.subscription) return

		this.tooltipEl$ = new BehaviorSubject<HTMLElement | null>(null)

		// Enter/leave over the trigger and, once it exists, the tooltip itself —
		// so a pointer that leaves the trigger onto the tooltip's own rect
		// resolves through the SAME pipeline rather than a second subscription.
		// No grace timer: the border-as-gap technique in `reveal()` already
		// keeps the two hit rects contiguous, so there is no dead space between
		// a `pointerleave` on one and a `pointerenter` on the other to bridge.
		const hovering$ = this.tooltipEl$.pipe(
			switchMap(tooltipEl =>
				merge(
					fromEvent<PointerEvent>(element, 'pointerenter'),
					fromEvent<PointerEvent>(element, 'pointerleave'),
					...(tooltipEl
						? [fromEvent<PointerEvent>(tooltipEl, 'pointerenter'), fromEvent<PointerEvent>(tooltipEl, 'pointerleave')]
						: []),
				),
			),
			filter(e => e.pointerType !== 'touch'),
			map(e => e.type === 'pointerenter'),
			startWith(false),
			distinctUntilChanged(),
		)

		const focused$ = merge(
			fromEvent(element, 'focusin').pipe(
				filter(() => element.matches(':focus-visible')),
				map(() => true),
			),
			fromEvent(element, 'focusout').pipe(map(() => false)),
		).pipe(startWith(false), distinctUntilChanged())

		// combineLatest — never two independent sessions — so the two arms
		// resolve to ONE reason; hover's teardown can never close a tooltip
		// keyboard focus still holds open, since a live `focused` always wins.
		this.subscription = combineLatest([hovering$, focused$])
			.pipe(
				map(([hovering, focused]): OpenReason => (focused ? 'keyboard' : hovering ? 'pointer' : null)),
				distinctUntilChanged(),
				// Empty text is "no tooltip" — a consumer whose text is conditionally
				// blank (a rail item with tooltips off, say) attaches this directive
				// unconditionally and controls visibility purely through `text`,
				// with no separate on/off flag to keep in sync.
				switchMap(reason =>
					reason === null || !this.text
						? of(null).pipe(tap(() => this.hide()))
						: // `takeUntil` FIRST: it subscribes its notifier before the timer
							// starts, so the pending window is guarded from its own first
							// instant — see the file header's "The press half of that arms
							// EARLIER" paragraph. A press here cancels the scheduled reveal
							// outright rather than hiding anything: nothing was ever shown, so
							// `visible` never went true, no floating element was built and no
							// `aria-describedby` was written. It stays cancelled until the
							// reason itself changes (the pointer leaving and returning, say) —
							// exactly what dismissing an already-shown tooltip leaves behind.
							timer(reason === 'keyboard' ? 0 : this.delay).pipe(
								takeUntil(fromEvent<PointerEvent>(element, 'pointerdown')),
								tap(() => this.reveal()),
							),
				),
			)
			.subscribe()
	}

	private reveal(): void {
		const element = this.element
		// A pending timer must not build an element for a host already gone —
		// `isConnected` is `AsyncDirective`'s own documented contract.
		if (!element || !this.isConnected) return

		// Only a genuine hidden→shown transition runs the entrance below (see
		// the file header's "A canceled WAAPI animation…" paragraph) — a
		// reveal() call arriving while the tooltip is ALREADY visible (the
		// pipeline re-emits on any `OpenReason` change, not only `null →
		// reason`) touches no animation state at all. Every other step below
		// (positioning, aria, escape/dismiss listeners) still runs regardless,
		// since none of it depends on which reason currently holds the
		// tooltip open.
		const wasHidden = !this.visible

		// `let` locals (not `this.X` re-reads) so TS narrows them to non-null
		// straight through the rest of this method with no cast.
		let tooltipElement = this.tooltipElement
		let textNode = this.textNode
		let pillElement = this.pillElement

		if (!tooltipElement || !textNode || !pillElement) {
			const root = element.getRootNode()
			// Forced by CSS anchor positioning's tree-scoped `anchor-name`
			// lookup — see the file header. A plain shadow-agnostic
			// `document.body` append is the exact bug this rewrite exists to
			// make structurally impossible for a shadow-DOM trigger.
			const container = root instanceof ShadowRoot ? root : document.body
			const uid = Math.random().toString(36).slice(2, 9)

			tooltipElement = document.createElement('div')
			tooltipElement.className = 'schmancy-tooltip'
			tooltipElement.id = `tooltip-${uid}`
			tooltipElement.setAttribute('role', 'tooltip')
			// House pattern: property assignment, not setAttribute — 'hint' so
			// a second tooltip opening elsewhere on the page can auto-close this
			// one, without ever touching schmancy's own 'manual' overlay stack
			// (hint only auto-dismisses other hints). Chromium implements
			// 'hint'; WebKit's invalid-value default resolves it to 'manual',
			// so two 'hint' tooltips can both stay visible there — same
			// engine caveat as Escape above.
			tooltipElement.popover = 'hint'
			Object.assign(tooltipElement.style, {
				// The UA popover stylesheet's `inset: 0; margin: auto` is what
				// leaves an anchor-positioned popover centered on the viewport
				// instead of at its computed position — both overrides are
				// load-bearing, not decorative.
				inset: 'auto',
				margin: '0',
				// The hit-area gap only — no paint of its own. Everything
				// visible lives on the nested pill below.
				border: `${GAP_PX}px solid transparent`,
				// The UA popover sheet paints `background-color: canvas` (an
				// OPAQUE system colour — rgb(18,18,18) in Chrome's dark scheme).
				// Left in force it draws a filled rectangle out to this box's
				// border edge, i.e. a GAP_PX-thick slab framing the pill on every
				// side. Transparent is what makes "no paint of its own" true.
				background: 'transparent',
				pointerEvents: 'auto',
				// WAAPI (`element.animate()` below), not a CSS transition —
				// `transition: none` keeps this file's own show/hide free of
				// any accidental interaction with an author transition.
				transition: 'none',
			})

			// The actual painted pill, nested one level inside the hit-area box
			// above — never painted directly on `tooltipElement`. Not because of
			// box-shadow bleed (there is none any more — see the file header's
			// "the pill itself is a real schmancy-surface" paragraph); because
			// the GAP_PX transparent border is the WCAG-hoverable hit-box
			// technique's whole mechanism, and `<schmancy-surface>` owns its own
			// background paint — putting the border on the surface itself would
			// mean fighting that paint (e.g. `background-clip: padding-box`) to
			// keep it out of the gap, coupling this file to the primitive's
			// internal implementation instead of just consuming its documented
			// `type`/`opacity`/`elevated` API. Animating `tooltipElement`'s own
			// opacity/transform below still fades and scales this nested pill
			// along with it — a transform or opacity on an ancestor composites
			// its whole painted subtree — so nothing about the show/hide
			// animation changes.
			// A dedicated `const` (not the outer `let pillElement`) so its type
			// stays the real `SchmancySurface` — reassigning the wider-typed
			// `let` here would widen straight back to `HTMLElement`, losing
			// `.type`/`.opacity`/`.elevated`/`.updateComplete` and forcing a cast.
			const surfaceElement = document.createElement('schmancy-surface')
			surfaceElement.type = 'containerHighest'
			surfaceElement.opacity = 'solid'
			surfaceElement.elevated = true
			Object.assign(surfaceElement.style, {
				// A pure paint layer, never the hit target: `tooltipElement`
				// (its pointer-events:'auto' above) is what the rest of this
				// file treats as canonical — the box the hover/focus pipeline
				// listens on, the box `aria-describedby` and `role="tooltip"`
				// point at. Left interactive, this child would be the deepest
				// element under a real pointer and hit-test AS ITSELF instead
				// of the outer box every other piece of this file already
				// expects to be "the tooltip".
				pointerEvents: 'none',
				// Sizing/shape is the consumer's job per `<schmancy-surface>`'s
				// own docblock — background, text colour, and elevation are the
				// only things the primitive itself owns.
				padding: 'var(--schmancy-sys-spacing-2, 8px) var(--schmancy-sys-spacing-3, 12px)',
				borderRadius: TOOLTIP_RADIUS,
				maxWidth: 'min(300px, calc(100vw - 16px))',
			})
			pillElement = surfaceElement

			// The type scale: MD3 body-medium (14px/20px/400), the exact values
			// <schmancy-typography type="body" token="md"> hardcodes in its own
			// static CSS for that role — owned by the primitive now, not copied
			// literals. `align="center"` is typography's own concern (its
			// `:host([align='center'])` rule), not a layout property the surface
			// or this file should carry.
			const typographyElement = document.createElement('schmancy-typography')
			typographyElement.type = 'body'
			typographyElement.token = 'md'
			typographyElement.align = 'center'

			// A child text node, never `typographyElement.textContent` — that
			// setter would fight any future non-text child the same way it did
			// the arrow this design no longer has.
			textNode = document.createTextNode(this.text)
			typographyElement.appendChild(textNode)
			surfaceElement.appendChild(typographyElement)
			tooltipElement.appendChild(surfaceElement)
			container.appendChild(tooltipElement)

			this.tooltipElement = tooltipElement
			this.pillElement = pillElement
			this.textNode = textNode
			this.tooltipEl$.next(tooltipElement)

			if (CAPS.cssAnchor) {
				this.anchorName = `--tt-${uid}`
				tooltipElement.style.positionAnchor = this.anchorName
				tooltipElement.style.positionTryFallbacks = 'flip-block, flip-inline, flip-block flip-inline'
			} else {
				// `surfaceElement`/`typographyElement` are freshly built custom
				// elements: setting `type`/`opacity`/`elevated`/`token`/`align`
				// above only queues their first Lit update (reflection to the
				// attribute the `:host([type=…])` CSS actually matches on is
				// scheduled, not synchronous) — at least one microtask after
				// `connectedCallback`, never in the same synchronous pass as
				// `container.appendChild` above. The SYNCHRONOUS `positionByRect`
				// call below this if-block runs before that microtask, so on
				// this very first build it can measure the pill using its
				// pre-upgrade (default, non-MD3) font metrics and land at the
				// wrong height. Re-running the measurement once both nested
				// primitives report their own first update complete corrects
				// it — still inside the SAME frame (microtasks fully drain
				// before the browser paints), so the wrong-metric position is
				// scheduled but never actually painted. Guarded against a
				// rebuild/detach racing this microtask, however unlikely, since
				// both null `this.tooltipElement` on teardown and replace it on
				// a fresh build.
				const builtTooltipElement = tooltipElement
				Promise.all([surfaceElement.updateComplete, typographyElement.updateComplete]).then(() => {
					if (this.tooltipElement === builtTooltipElement) positionByRect(element, builtTooltipElement, this.position)
				})
			}
		}

		// Frozen into a `const` (not read back off the mutable `let` above) so
		// the fallback-reposition closure below captures a type TS can prove
		// stays non-null, with no cast.
		const tip = tooltipElement
		textNode.data = this.text
		if (CAPS.cssAnchor) tip.style.positionArea = this.position

		// Append-not-overwrite, and re-asserted on EVERY reveal (not only the
		// first build) — see the file header's `anchor-name` paragraph. Only
		// ever runs when `this.anchorName` is set, which happens exactly when
		// `CAPS.cssAnchor` is true (a frozen constant, so that never flips
		// mid-session).
		if (this.anchorName) {
			const anchorName = this.anchorName
			const tokens = element.style.anchorName
				.split(',')
				.map(t => t.trim())
				.filter(t => t && t !== 'none')
			if (!tokens.includes(anchorName)) tokens.push(anchorName)
			element.style.anchorName = tokens.join(', ')
		}

		this.visible = true
		try {
			tip.showPopover()
		} catch {
			// Already open — a reveal can race a pending hide's own teardown.
		}

		if (!CAPS.cssAnchor) {
			// showPopover() above already ran synchronously; measuring now,
			// before this task yields to the event loop, means the browser
			// paints only the final position — never the wrong one. Measured
			// BEFORE the entrance below starts its transform, though
			// `positionByRect` itself is transform-proof either way (see its
			// own comment) since the scroll/resize resubscription below can
			// re-measure mid-animation on a later scroll.
			positionByRect(element, tip, this.position)
			this.fallbackReposition?.unsubscribe()
			this.fallbackReposition = merge(
				fromEvent(window, 'scroll', { capture: true }),
				fromEvent(window, 'resize'),
			).subscribe(() => positionByRect(element, tip, this.position))
		}

		// A re-show restores hit-testing a prior hide() may have switched off
		// (below) mid-exit.
		tip.style.pointerEvents = 'auto'

		// Scale + opacity, never a directional translate — see the file
		// header. `fill: 'forwards'` (inside `createAnimation`) pins the end
		// keyframe (opacity 1, scale 1 — the identity transform) after this
		// finishes, so it never fights the positioning above. Reduced-motion
		// collapses this to a 0-duration snap on its own (`createAnimation`).
		// Runs only on a genuine hidden→shown transition (`wasHidden`, above)
		// — an already-visible tooltip whose `reason` merely changed leaves
		// whatever animation is currently running (or already finished)
		// completely untouched.
		if (wasHidden) {
			// A re-show racing a pending exit reads that exit's own live
			// progress before replacing it, so the entrance below picks up
			// from wherever the box actually was — never the fixed 0/0.9
			// keyframe regardless of how far the exit had already faded (see
			// the file header's "A canceled WAAPI animation…" paragraph). No
			// animation in flight (a genuine first show) falls back to that
			// same fixed 0/0.9 the entrance has always started from.
			const fromState = this.currentAnimation ? readLiveScaleState(tip) : { opacity: 0, scale: 0.9 }
			this.exitSubscription?.unsubscribe()
			this.exitSubscription = undefined
			this.currentAnimation?.cancel()

			const entrance = createAnimation(SPRING_SNAPPY, {
				from: { opacity: fromState.opacity, transform: `scale(${fromState.scale})` },
				to: { opacity: 1, transform: 'scale(1)' },
			})
			this.currentAnimation = tip.animate(entrance.keyframes, entrance.options)
		}

		// Appends the tooltip's id as a space-separated token — never
		// clobbering a pre-existing `aria-describedby` the consumer already set.
		if (!this.describedByAdded) {
			const ids = (element.getAttribute('aria-describedby') ?? '').split(' ').filter(Boolean)
			if (!ids.includes(tip.id)) {
				ids.push(tip.id)
				element.setAttribute('aria-describedby', ids.join(' '))
			}
			this.describedByAdded = true
		}

		this.escapeSubscription?.unsubscribe()
		this.escapeSubscription = fromEvent<KeyboardEvent>(document, 'keydown').subscribe(e => {
			if (e.key === 'Escape') this.hide()
		})

		// Registered only while shown — see the file header's "That guarantee
		// only runs the one direction" paragraph, which also covers why this is
		// two composed signals rather than one `toggle` listener. Plus
		// `pointerdown` on the trigger itself — both native tooltip behaviour
		// and the common path: hover a button, then click it to open what it
		// controls. That press arm is the SHOWN half of a pair: `attach()`
		// carries the other, over the window where the show is still on its
		// timer, so between them no press ever falls through.
		this.dismissSubscription?.unsubscribe()
		this.dismissSubscription = merge(
			fromEvent<ToggleEvent>(document, 'toggle', { capture: true }).pipe(
				filter(e => e.newState === 'open' && e.target !== tip),
			),
			fromEvent<FocusEvent>(document, 'focusin').pipe(
				filter(e => {
					const origin = e.composedPath()[0]
					return (
						origin instanceof Element &&
						origin !== element &&
						origin !== tip &&
						origin.closest(':popover-open') !== null
					)
				}),
			),
			fromEvent<PointerEvent>(element, 'pointerdown'),
		).subscribe(() => this.hide())
	}

	private hide(): void {
		if (!this.visible) return
		this.visible = false

		const tooltipElement = this.tooltipElement
		if (tooltipElement) {
			// The exit continues from wherever the box's own live opacity/scale
			// actually are, read before `cancel()` discards whatever animation
			// is currently in flight — the entrance mid-fade, or a settled one
			// sitting at its finished 1/1 fill (see the file header's "A
			// canceled WAAPI animation…" paragraph). A fixed 1/1 "from" is only
			// ever correct for that settled case; every other one it flashes
			// the box to full brightness/size for one frame on its way out. No
			// animation in flight falls back to that same settled 1/1.
			const fromState = this.currentAnimation ? readLiveScaleState(tooltipElement) : { opacity: 1, scale: 1 }
			this.currentAnimation?.cancel()
			// A dismissed tooltip is committed to closing — its own box is
			// mid-flight (shrinking via the exit's scale transform below), and
			// a real cursor resting anywhere near that shrinking/growing edge
			// would otherwise fire a genuine pointerenter/pointerleave from the
			// geometry change alone, not from the user moving at all, which
			// `hovering$` (the SAME box's own pointerenter/pointerleave, kept
			// for WCAG 2.2 SC 1.4.13 Hoverable) would read as a fresh hover and
			// resurrect the very tooltip this exit is closing. Switching hit-
			// testing off for the exit's duration closes that loop at its root
			// — `reveal()` restores it on any later re-show.
			tooltipElement.style.pointerEvents = 'none'
			// SPRING_SNAPPY's own easing at DURATION_EXIT's shorter duration —
			// an exit reads correct leaving faster than it arrived.
			const exit = createAnimation(
				{ ...SPRING_SNAPPY, duration: DURATION_EXIT },
				{
					from: { opacity: fromState.opacity, transform: `scale(${fromState.scale})` },
					to: { opacity: 0, transform: 'scale(0.9)' },
				},
			)
			const animation = tooltipElement.animate(exit.keyframes, exit.options)
			this.currentAnimation = animation

			// `hidePopover()` runs only once THIS exit's own `finished` resolves
			// — never a CSS `transition-behavior: allow-discrete` + `overlay`
			// transition (see the file header). `finished` REJECTS when a
			// re-show or teardown cancels this animation out from under it, so
			// `catchError` is the "superseded, not an error" branch — whichever
			// won owns closing/removing the popover instead.
			this.exitSubscription?.unsubscribe()
			this.exitSubscription = from(animation.finished)
				.pipe(
					take(1),
					tap(() => {
						try {
							tooltipElement.hidePopover()
						} catch {
							// Already hidden — e.g. the engine's own light-dismiss beat
							// us to it on an engine that implements `hint`. Fine either way.
						}
					}),
					catchError(() => EMPTY),
				)
				.subscribe()

			this.removeDescribedBy(tooltipElement)
		}

		this.fallbackReposition?.unsubscribe()
		this.fallbackReposition = undefined
		this.escapeSubscription?.unsubscribe()
		this.escapeSubscription = undefined
		this.dismissSubscription?.unsubscribe()
		this.dismissSubscription = undefined
	}

	/** Removes exactly the token this directive added, never blanket-removing
	 *  the attribute — a pre-existing value survives. */
	private removeDescribedBy(tooltipElement: HTMLElement): void {
		const element = this.element
		if (!element || !this.describedByAdded) return

		const ids = (element.getAttribute('aria-describedby') ?? '').split(' ').filter(id => id && id !== tooltipElement.id)
		if (ids.length > 0) element.setAttribute('aria-describedby', ids.join(' '))
		else element.removeAttribute('aria-describedby')
		this.describedByAdded = false
	}

	private detach(): void {
		this.subscription?.unsubscribe()
		this.subscription = undefined

		this.fallbackReposition?.unsubscribe()
		this.fallbackReposition = undefined
		this.escapeSubscription?.unsubscribe()
		this.escapeSubscription = undefined
		this.dismissSubscription?.unsubscribe()
		this.dismissSubscription = undefined
		this.exitSubscription?.unsubscribe()
		this.exitSubscription = undefined

		// An unmounting host must not leave an animating node behind — cancel
		// whatever is in flight and hide synchronously rather than animating
		// out, the one place this file's show/hide skips WAAPI entirely.
		this.currentAnimation?.cancel()
		this.currentAnimation = null
		if (this.tooltipElement) {
			try {
				this.tooltipElement.hidePopover()
			} catch {
				// Already hidden.
			}
			this.removeDescribedBy(this.tooltipElement)
		}
		// Removes exactly this instance's own token from the list, mirroring
		// `removeDescribedBy` — never a blanket `removeProperty`, which would
		// also strip any other writer's token sharing the same property.
		if (this.anchorName && this.element) {
			const anchorName = this.anchorName
			const tokens = this.element.style.anchorName
				.split(',')
				.map(t => t.trim())
				.filter(t => t && t !== 'none' && t !== anchorName)
			if (tokens.length > 0) this.element.style.anchorName = tokens.join(', ')
			else this.element.style.removeProperty('anchor-name')
		}

		this.tooltipElement?.remove()
		this.tooltipElement = null
		this.pillElement = null
		this.textNode = null
		this.anchorName = null
		this.visible = false
	}

	override disconnected(): void {
		this.detach()
	}

	override reconnected(): void {
		if (!this.subscription) this.attach()
	}
}

/**
 * Reads a WAAPI-animated tooltip's OWN opacity/scale as the browser is
 * currently painting them — called immediately before a running animation is
 * canceled and replaced (`reveal()`'s re-show-mid-exit path, `hide()`'s own
 * cancel), so the replacement starts from wherever the box actually is
 * rather than a fixed keyframe endpoint (the file header's "A canceled WAAPI
 * animation…" paragraph). The tooltip's own WAAPI animations only ever apply
 * a uniform `scale(n)`, so the 2D matrix's first value IS the scale factor;
 * `none` (the identity transform, nothing running) reads as 1.
 */
function readLiveScaleState(tip: HTMLElement): { opacity: number; scale: number } {
	const computed = getComputedStyle(tip)
	const { transform } = computed
	const match = transform === 'none' ? null : /matrix\(([^,]+),/.exec(transform)
	return {
		opacity: parseFloat(computed.opacity),
		scale: match ? parseFloat(match[1]) : 1,
	}
}

/**
 * Feature-detection fallback for engines without CSS anchor positioning: one
 * rect read at show time, refreshed on capture-phase scroll/resize while the
 * tooltip stays open — the JS equivalent of what anchor positioning resolves
 * for free, minus `@floating-ui/dom`'s per-frame `autoUpdate` loop.
 */
function positionByRect(trigger: HTMLElement, tip: HTMLElement, position: TooltipOptions['position']): void {
	const t = trigger.getBoundingClientRect()
	// offsetWidth/offsetHeight, not tip.getBoundingClientRect() — the entrance/
	// exit animation's own `transform: scale()` would otherwise contaminate a
	// read taken mid-animation (e.g. this function's own scroll/resize
	// resubscription firing while the entrance is still running) with the
	// tip's CURRENT scaled size rather than its true one; offset* is the
	// layout size, transform-invariant by definition.
	const width = tip.offsetWidth
	const height = tip.offsetHeight
	let left = t.left + t.width / 2 - width / 2
	let top = t.top + t.height / 2 - height / 2
	if (position === 'top') top = t.top - height
	else if (position === 'bottom') top = t.bottom
	else if (position === 'left') left = t.left - width
	else if (position === 'right') left = t.right
	left = Math.min(Math.max(8, left), window.innerWidth - width - 8)
	top = Math.min(Math.max(8, top), window.innerHeight - height - 8)
	Object.assign(tip.style, { left: `${left}px`, top: `${top}px` })
}

export const tooltip = directive(TooltipDirective)
